import api from './api';

export interface Customer {
  id: string;
  email: string;
  name: string;
  phone?: string;
  catalogAccessLevel: 'new' | 'paid_deposit' | 'exclusive' | 'partner';
  emailVerified: boolean;
  acceptedTerms: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  photoCount?: number;
  sessionCount?: number;
  photos?: Photo[];
  styleMatcherSessions?: StyleMatcherSession[];
  locationId?: string | null;
  location?: { id: string; name: string } | null;
}

export interface Photo {
  id: string;
  customerId: string;
  dressId?: string;
  originalUrl: string;
  thumbnailUrl: string;
  uploadedBy: string;
  notes?: string;
  createdAt: string;
  uploader?: {
    id: string;
    name: string;
  };
  dress?: {
    id: string;
    name: string;
  };
}

export interface StyleMatcherSession {
  id: string;
  customerId: string;
  startedAt: string;
  completedAt?: string;
  totalSwipes: number;
  likesCount: number;
  passesCount: number;
  skipsCount: number;
  swipes?: StyleMatcherSwipe[];
}

export interface StyleMatcherSwipe {
  id: string;
  sessionId: string;
  customerId: string;
  dressId: string;
  action: 'like' | 'pass' | 'skip';
  swipedAt: string;
  dress?: {
    id: string;
    name: string;
    categories: string[];
  };
}

export interface CustomerFilters {
  page?: number;
  limit?: number;
  search?: string;
  accessLevel?: 'new' | 'paid_deposit' | 'exclusive' | 'partner';
  emailVerified?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UpdateCustomerRequest {
  catalogAccessLevel?: 'new' | 'paid_deposit' | 'exclusive' | 'partner';
  phone?: string;
  name?: string;
  locationId?: string | null;
}

class CustomerService {
  async getCustomers(filters: CustomerFilters = {}): Promise<PaginatedResponse<Customer>> {
    const response = await api.get('/customers', { params: filters });
    return response.data;
  }

  async getCustomer(id: string): Promise<Customer> {
    const response = await api.get(`/customers/${id}`);
    return response.data.data;
  }

  async updateCustomer(id: string, data: UpdateCustomerRequest): Promise<Customer> {
    const response = await api.put(`/customers/${id}`, data);
    return response.data.data;
  }

  // Admin only: creates a partner account (sees only partner-tagged dresses).
  // The partner receives a set-password email.
  async createPartner(data: { email: string; name: string; phone?: string; locationId?: string }): Promise<Customer> {
    const response = await api.post('/customers/partner', data);
    return response.data.data;
  }

  async resendVerificationEmail(id: string): Promise<{ message: string }> {
    const response = await api.post(`/customers/${id}/resend-verification`);
    return response.data.data;
  }

  async verifyEmailManually(id: string): Promise<{ message: string }> {
    const response = await api.post(`/customers/${id}/verify-email`);
    return response.data.data;
  }

  async getCustomerPhotos(id: string, page = 1, limit = 20): Promise<PaginatedResponse<Photo>> {
    const response = await api.get(`/customers/${id}/photos`, { params: { page, limit } });
    return response.data;
  }

  async getCustomerStyleMatcher(id: string): Promise<{
    sessions: StyleMatcherSession[];
    categoryPreferences: Record<string, number>;
  }> {
    const response = await api.get(`/customers/${id}/style-matcher`);
    return response.data.data;
  }

  async updateCustomerStatus(id: string, isActive: boolean): Promise<{ message: string; customer: { id: string; isActive: boolean } }> {
    const response = await api.put(`/customers/${id}/status`, { isActive });
    return response.data.data;
  }

  async deleteCustomer(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/customers/${id}`);
    return response.data.data;
  }

  async exportCustomers(filters: Omit<CustomerFilters, 'page' | 'limit'> = {}): Promise<{
    data: Array<Customer & {
      photoCount: number;
      styleMatcherSessionCount: number;
      lastStyleMatcherSession: string | null;
    }>;
    exportedAt: string;
    totalCount: number;
  }> {
    const response = await api.get('/customers/export', { params: filters });
    return response.data;
  }

  async getCustomerTagPreferences(customerId: string): Promise<CustomerTagPreferences> {
    const response = await api.get(`/dress-likes/customer/${customerId}/tag-preferences`);
    return response.data.data;
  }
}

export interface TagPreference {
  name: string;
  count: number;
  categoryId: string;
}

export interface LikedDress {
  id: string;
  likedAt: string;
  dress: {
    id: string;
    name: string;
    description?: string;
    purchasePrice: number;
    rentalPrice: number;
    imageUrl?: string;
    thumbnailUrl?: string;
    tiers: Array<'new' | 'paid_deposit' | 'exclusive' | 'partner'>;
    designer: {
      id: string;
      name: string;
    };
    dressCategories: Array<{
      id: string;
      name: string;
      displayName: string;
    }>;
  };
}

export interface CustomerTagPreferences {
  customer: {
    id: string;
    name: string;
    email: string;
  };
  totalLikedDresses: number;
  tagPreferences: TagPreference[];
  likedDresses: LikedDress[];
}

const customerService = new CustomerService();
export { customerService };
export default customerService;